import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Boxes, 
  Layers, 
  Search, 
  Filter, 
  ArrowLeft, 
  CheckSquare, 
  Square, 
  ChevronRight, 
  AlertTriangle, 
  Clock, 
  Plus, 
  X,
  RefreshCw,
  Eye,
  CheckCircle,
  RotateCcw,
  Sparkles,
  Trash2,
  Check,
  Package,
  ClipboardList,
  XCircle
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { loteService } from "../services/loteService";
import { stockProductionService } from "../services/stockProductionService";
import { useOperator } from "../contexts/OperatorContext";
import { useCatalogContext } from "../contexts/CatalogContext";
import { resolveCatalogSku } from "../utils/skuUtils";
import ItemCard from "../components/production/ItemCard";
import LoteSummary from "../components/production/LoteSummary";
import useDisponibilidade from "../hooks/useDisponibilidade";
import StockProductionTab from "./StockProductionTab";

// Status do Fluxo da Oficina
const PE_STATUS_CONFIG = [
  { id: "pe_solicitado", label: "Solicitado / Fila", color: "border-slate-300", bg: "bg-slate-50", text: "text-slate-700" },
  { id: "pe_imprimindo", label: "Impressão 3D", color: "border-blue-400", bg: "bg-blue-50", text: "text-blue-700" },
  { id: "pe_fundicao", label: "Fundição", color: "border-orange-400", bg: "bg-orange-50", text: "text-orange-700" },
  { id: "pe_revisao", label: "Revisão / Acabamento", color: "border-amber-400", bg: "bg-amber-50", text: "text-amber-700" },
  { id: "pe_conferencia", label: "Conferência", color: "border-purple-400", bg: "bg-purple-50", text: "text-purple-700" }
];

export default function FabricaSubTab({ user, findCatalogItem, inventory, onRefresh }) {
  const { isSharedTerminal, terminalType, activeOperator } = useOperator();
  const isOficina = isSharedTerminal && terminalType === "OFICINA";
  
  const { catalog: catalogMap } = useCatalogContext();
  const [showPendingPanel, setShowPendingPanel] = useState(false);
  const [rejectingLote, setRejectingLote] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvingLoteId, setApprovingLoteId] = useState(null);

  const [viewMode, setViewMode] = useState("lotes"); // 'lotes' | 'transversal'
  const [activeStatusTab, setActiveStatusTab] = useState("TODOS"); // 'TODOS' | status_id
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [canceledLoteIds, setCanceledLoteIds] = useState(new Set());
  const [selectedLoteId, setSelectedLoteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOrigem, setFilterOrigem] = useState("TODOS");
  
  // Seleção múltipla para ações em massa
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  // Modal de Criação Manual de Lote
  const [showCreateLoteModal, setShowCreateLoteModal] = useState(false);
  const [newLoteCodigo, setNewLoteCodigo] = useState("");
  const [newLoteItems, setNewLoteItems] = useState([{ sku: "", qtd: 1 }]);

  // Modal de Análise Pontual de SKU (LAZY)
  const [analyzingSku, setAnalyzingSku] = useState(null);

  // Modal de Suspensão de Peça
  const [suspendModalItem, setSuspendModalItem] = useState(null);
  const [motivosSuspensao, setMotivosSuspensao] = useState([]);
  const [selectedMotivoId, setSelectedMotivoId] = useState("");
  const [suspendObs, setSuspendObs] = useState("");
  const [isSuspending, setIsSuspending] = useState(false);

  // Modal de Suspensão de Lote Inteiro
  const [showSuspendLoteModal, setShowSuspendLoteModal] = useState(false);
  const [suspendLoteObs, setSuspendLoteObs] = useState("");
  const [selectedLoteMotivoId, setSelectedLoteMotivoId] = useState("");
  const [isSuspendingLote, setIsSuspendingLote] = useState(false);

  // Modal de Solicitação da Oficina
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestSku, setRequestSku] = useState("");
  const [requestQty, setRequestQty] = useState(1);
  const [requestMotivo, setRequestMotivo] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

  // Carrega lotes ativos, suspensos e pendentes
  const loadLotes = useCallback(async () => {
    try {
      setLoadingLotes(true);
      const res = await pb.collection("estoque_lotes").getFullList({
        filter: 'status = "em_producao" || status = "suspenso" || status = "aguardando_aprovacao"',
        sort: "-created"
      });
      setLotes(res);
    } catch (e) {
      console.error("Erro ao carregar lotes da fábrica:", e);
    } finally {
      setLoadingLotes(false);
    }
  }, []);

  // Carrega motivos de suspensão
  const loadMotivos = useCallback(async () => {
    try {
      const res = await pb.collection("estoque_suspensao_motivos").getFullList({
        filter: "ativo = true",
        sort: "nome"
      });
      setMotivosSuspensao(res);
    } catch (e) {
      console.error("Erro ao carregar motivos de suspensão:", e);
    }
  }, []);

  // Carrega lotes inativos/cancelados/suspensos
  const loadCanceledLotes = useCallback(async () => {
    try {
      const res = await pb.collection("estoque_lotes").getFullList({
        filter: 'status = "cancelado" || status = "suspenso"',
        fields: "id"
      });
      setCanceledLoteIds(new Set(res.map(r => r.id)));
    } catch (e) {
      console.error("Erro ao carregar lotes inativos:", e);
    }
  }, []);

  useEffect(() => {
    loadLotes();
    loadMotivos();
    loadCanceledLotes();
  }, [loadLotes, loadMotivos, loadCanceledLotes]);

  // Lista de itens ativos na produção
  const activeItems = useMemo(() => {
    if (!Array.isArray(inventory)) return [];
    return inventory.filter(i => 
      i.isPE && 
      !["in_stock", "sold", "adjusted_out", "pe_interceptado"].includes(i.status) &&
      i.situacao_fluxo !== "suspenso"
    );
  }, [inventory]);

  // Peças filtradas para a Visão Transversal Clássica
  const filteredTransversalItems = useMemo(() => {
    return activeItems.filter(item => {
      const matchStatus = activeStatusTab === "TODOS" || item.status === activeStatusTab;
      const matchSearch = searchTerm === "" || 
        item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ((item.name || "").toLowerCase().includes(searchTerm.toLowerCase()));
      return matchStatus && matchSearch;
    });
  }, [activeItems, activeStatusTab, searchTerm]);

  // Seleção global para visão transversal
  const toggleSelectAllTransversal = () => {
    if (selectedItemIds.size === filteredTransversalItems.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(filteredTransversalItems.map(i => i.id)));
    }
  };

  // Conjunto de SKUs com BOM
  const bomSkuSet = useMemo(() => {
    return new Set();
  }, []);

  // Lote Virtual "AVULSO" para agregar peças físicas sem lote vinculado
  const avulsoLote = useMemo(() => {
    const avulsoItems = activeItems.filter(i => !i.lote || i.lote === "" || i.lote === "avulso" || i.lote === "AVULSO");
    if (avulsoItems.length === 0) return null;
    return {
      id: "AVULSO",
      codigo: "AVULSO",
      status: "em_producao",
      origem: "avulso",
      observacoes: "Peças físicas na fábrica sem lote atribuído",
      isAvulso: true,
      total_pecas: avulsoItems.length
    };
  }, [activeItems]);

  // Solicitações pendentes de lotes (aguardando aprovação)
  const pendingLotes = useMemo(() => {
    return lotes.filter(l => l.status === "aguardando_aprovacao" || l.status === "pendente");
  }, [lotes]);

  // Filtragem de lotes ativos para a Visão por Lotes (exclui pendentes do grid gerencial, eles ficam no topo)
  const activeLotesFiltered = useMemo(() => {
    const list = lotes.filter(l => l.status === "em_producao" || l.status === "suspenso").filter(l => {
      const matchSearch = searchTerm === "" || 
        (l.codigo && l.codigo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (l.observacoes && l.observacoes.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchOrigem = filterOrigem === "TODOS" || l.origem === filterOrigem;
      return matchSearch && matchOrigem;
    });

    if (avulsoLote) {
      return [avulsoLote, ...list];
    }
    return list;
  }, [lotes, searchTerm, filterOrigem, avulsoLote]);

  // Lote selecionado no detalhe
  const selectedLote = useMemo(() => {
    if (!selectedLoteId) return null;
    if (selectedLoteId === "AVULSO") return avulsoLote;
    return lotes.find(l => l.id === selectedLoteId) || null;
  }, [lotes, selectedLoteId, avulsoLote]);

  // Peças do lote selecionado (inclui suspensas para lotes específicos)
  const selectedLoteItems = useMemo(() => {
    if (!selectedLoteId) return [];
    if (!Array.isArray(inventory)) return [];
    if (selectedLoteId === "AVULSO") {
      return activeItems.filter(i => !i.lote || i.lote === "" || i.lote === "avulso" || i.lote === "AVULSO");
    }
    return inventory.filter(i => 
      i.isPE === true && 
      !["in_stock", "sold", "adjusted_out", "pe_interceptado"].includes(i.status) &&
      i.lote === selectedLoteId
    );
  }, [inventory, activeItems, selectedLoteId]);

  // Cálculo de progresso e estatísticas do lote selecionado
  const selectedLoteStats = useMemo(() => {
    if (!selectedLote) return { progress: { percent: 0, concluidas: 0, interceptadas: 0, suspensas: 0, total: 0 }, costs: { expected: 0, effective: 0 }, regressions: { total: 0 } };

    const loteItems = selectedLote.id === "AVULSO"
      ? activeItems.filter(i => !i.lote || i.lote === "" || i.lote === "avulso" || i.lote === "AVULSO")
      : inventory.filter(i => i.lote === selectedLote.id);
    const total = loteItems.length;
    const concluidas = loteItems.filter(i => i.status === "in_stock" || i.situacao_fluxo === "concluido").length;
    const interceptadas = loteItems.filter(i => i.situacao_fluxo === "interceptado").length;
    const suspensas = loteItems.filter(i => i.situacao_fluxo === "suspenso").length;
    const denominator = total - interceptadas - suspensas;
    const percent = denominator <= 0 ? 0 : Math.round((concluidas / denominator) * 100);

    let totalRegressions = 0;
    loteItems.forEach(item => {
      const history = Array.isArray(item.history) ? item.history : [];
      history.forEach(evt => {
        const toVal = evt.to || "";
        const textVal = evt.text || "";
        if (
          toVal === "pe_revisao" || 
          toVal === "pe_solicitado" || 
          toVal === "revisao" || 
          toVal === "solicitado" ||
          textVal.includes("pe_revisao") || 
          textVal.includes("pe_solicitado") || 
          textVal.includes("revisão") || 
          textVal.includes("solicitado")
        ) {
          totalRegressions++;
        }
      });
    });

    // Cálculo de insumos previstos e efetivos (S2.1)
    let totalExpected = 0;
    let totalEffective = 0;
    const suppliesMap = {};

    loteItems.forEach(item => {
      const cat = findCatalogItem ? findCatalogItem(item.sku) : null;
      const unitCost = Number(cat?.cost || cat?.preco_custo || cat?.full_data?.custo || 0);
      
      // Se a peça não está suspensa, contabiliza no efetivo
      if (item.situacao_fluxo !== "suspenso") {
        totalEffective += unitCost;
      }
      totalExpected += unitCost;

      // Insumos da receita (se definidos no catálogo)
      const bomList = cat?.bom || cat?.full_data?.bom || [];
      if (Array.isArray(bomList)) {
        bomList.forEach(ins => {
          const insName = ins.nome || ins.name || "Insumo";
          if (!suppliesMap[insName]) {
            suppliesMap[insName] = {
              nome: insName,
              unidade: ins.unit || ins.unidade || "un",
              qtdPrevista: 0,
              qtdEfetiva: 0,
              custoUnitario: Number(ins.custo_unitario || ins.cost || 0),
              custoTotal: 0
            };
          }
          const perUnit = Number(ins.quantity_per_unit || ins.qtd || 1);
          suppliesMap[insName].qtdPrevista += perUnit;
          if (item.situacao_fluxo !== "suspenso") {
            suppliesMap[insName].qtdEfetiva += perUnit;
            suppliesMap[insName].custoTotal += perUnit * suppliesMap[insName].custoUnitario;
          }
        });
      }
    });

    const suppliesReport = Object.values(suppliesMap);

    return {
      progress: { percent, concluidas, interceptadas, suspensas, total },
      costs: { expected: totalExpected, effective: totalEffective, suppliesReport },
      regressions: { total: totalRegressions }
    };
  }, [selectedLote, inventory, findCatalogItem]);

  // Ação: Selecionar / Desmarcar item
  const handleToggleSelect = useCallback((id) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Ação: Avançar status individual
  const handleAdvanceStatus = useCallback(async (itemId, newStatus) => {
    try {
      const author = activeOperator?.nome || user?.name || "Fábrica";
      await stockProductionService.advanceBatchStatus([itemId], newStatus, author);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao avançar status: " + e.message);
    }
  }, [activeOperator, user, onRefresh]);

  // Ação: Avanço em massa
  const handleBulkAdvanceStatus = useCallback(async (newStatus) => {
    if (selectedItemIds.size === 0) return;
    try {
      const author = activeOperator?.nome || user?.name || "Fábrica";
      await stockProductionService.advanceBatchStatus(Array.from(selectedItemIds), newStatus, author);
      setSelectedItemIds(new Set());
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao avançar peças em massa: " + e.message);
    }
  }, [selectedItemIds, activeOperator, user, onRefresh]);

  // Ação: Exclusão de peça sem lote
  const handleDeleteItem = useCallback(async (itemId) => {
    if (!window.confirm("Deseja realmente remover esta peça física?")) return;
    try {
      await stockProductionService.deleteBatch([itemId]);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao excluir peça: " + e.message);
    }
  }, [onRefresh]);

  // Ação: Abrir modal de suspensão
  const handleOpenSuspend = useCallback((itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;
    setSuspendModalItem(item);
    setSelectedMotivoId("");
    setSuspendObs("");
  }, [inventory]);

  // Confirmar Suspensão
  const handleConfirmSuspend = async () => {
    if (!suspendModalItem || !selectedMotivoId || isSuspending) return;

    const motivoObj = motivosSuspensao.find(m => m.id === selectedMotivoId);
    if (motivoObj && motivoObj.obs_obrigatoria && !suspendObs.trim()) {
      alert("Para este motivo, a observação detalhada é obrigatória!");
      return;
    }

    setIsSuspending(true);
    try {
      const author = activeOperator?.nome || user?.name || "Fábrica";
      await stockProductionService.suspendItem(
        suspendModalItem.id, 
        motivoObj?.nome || selectedMotivoId, 
        suspendObs, 
        author
      );
      setSuspendModalItem(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao suspender peça: " + e.message);
    } finally {
      setIsSuspending(false);
    }
  };

  // Reativar Peça
  const handleReactivateItem = useCallback(async (itemId) => {
    if (!window.confirm("Confirmar a reativação desta peça? Isso irá redebitar os insumos da receita BOM.")) return;
    try {
      setLoadingLotes(true);
      const author = activeOperator?.nome || user?.name || "Fábrica";
      await stockProductionService.reactivateItem(itemId, author);
      if (onRefresh) onRefresh();
      alert("Peça reativada com sucesso!");
    } catch (e) {
      alert("Erro ao reativar peça: " + e.message);
    } finally {
      setLoadingLotes(false);
    }
  }, [onRefresh, user, activeOperator]);

  // Converter Peça para Avulsa (Desvincular do Lote Cancelado)
  const handleConvertLoteToAvulso = useCallback(async (itemId) => {
    if (!window.confirm("Confirmar a desvinculação deste lote cancelado? A peça continuará a ser produzida como Avulsa.")) return;
    try {
      setLoadingLotes(true);
      const author = activeOperator?.nome || user?.name || "Fábrica";
      await stockProductionService.convertToAvulso(itemId, author);
      if (onRefresh) onRefresh();
      alert("Peça convertida para Avulsa com sucesso!");
    } catch (e) {
      alert("Erro ao converter peça para Avulsa: " + e.message);
    } finally {
      setLoadingLotes(false);
    }
  }, [onRefresh, user, activeOperator]);

  // Ação: Confirmar Suspensão do Lote Inteiro
  const handleConfirmSuspendLote = async () => {
    if (!selectedLoteId || !selectedLoteMotivoId || isSuspendingLote) return;
    const motivoObj = motivosSuspensao.find(m => m.id === selectedLoteMotivoId);
    if (motivoObj && motivoObj.obs_obrigatoria && !suspendLoteObs.trim()) {
      alert("Para este motivo, a observação detalhada é obrigatória!");
      return;
    }

    setIsSuspendingLote(true);
    try {
      const author = activeOperator?.nome || user?.name || "Fábrica";
      await loteService.suspendLote(
        selectedLoteId,
        motivoObj?.nome || selectedLoteMotivoId,
        suspendLoteObs,
        user
      );
      setShowSuspendLoteModal(false);
      setSelectedLoteMotivoId("");
      setSuspendLoteObs("");
      loadLotes();
      if (onRefresh) onRefresh();
      alert("Lote inteiro suspenso com sucesso!");
    } catch (e) {
      alert("Erro ao suspender lote: " + e.message);
    } finally {
      setIsSuspendingLote(false);
    }
  };

  // Ação: Reativar Lote Inteiro
  const handleReactivateLote = async () => {
    if (!selectedLoteId) return;
    if (!window.confirm("Confirmar a reativação de todas as peças suspensas deste lote? Isso irá redebitar os insumos da receita BOM.")) return;

    try {
      setLoadingLotes(true);
      await loteService.reactivateLote(selectedLoteId, user);
      loadLotes();
      if (onRefresh) onRefresh();
      alert("Lote reativado com sucesso!");
    } catch (e) {
      alert("Erro ao reativar lote: " + e.message);
    } finally {
      setLoadingLotes(false);
    }
  };

  // Excluir Lote Vazio
  const handleDeleteLote = async () => {
    if (!selectedLote || selectedLote.id === "AVULSO") return;
    if (!window.confirm(`Deseja excluir o lote ${selectedLote.codigo}? Esta operação é irreversível.`)) return;
    
    try {
      setLoadingLotes(true);
      await pb.collection("estoque_lotes").delete(selectedLote.id);
      alert("Lote excluído com sucesso!");
      setSelectedLoteId(null);
      loadLotes();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao excluir lote: " + e.message);
    } finally {
      setLoadingLotes(false);
    }
  };

  // Helper para contagem de peças no lote
  const getLoteItemCount = (loteId) => {
    if (loteId === "AVULSO") {
      return activeItems.filter(i => !i.lote || i.lote === "" || i.lote === "avulso" || i.lote === "AVULSO").length;
    }
    return inventory.filter(i => i.lote === loteId).length;
  };

  // Helper para obter a data formatada
  const getLoteDate = (lote) => {
    if (lote.id === "AVULSO") return "-";
    if (!lote.created) return "-";
    return new Date(lote.created).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  // Ação: Aprovar Lote Pendente
  const handleApproveLote = async (loteId) => {
    if (!window.confirm("Deseja realmente aprovar este lote e iniciar a produção física? Os insumos BOM serão debitados.")) return;

    setApprovingLoteId(loteId);
    try {
      await loteService.approveLote(loteId, user);
      alert("Lote aprovado com sucesso! Produção iniciada.");
      loadLotes();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao aprovar lote: " + e.message);
    } finally {
      setApprovingLoteId(null);
    }
  };

  // Ação: Confirmar Rejeição do Lote Pendente
  const handleConfirmRejectLote = async () => {
    if (!rejectingLote || !rejectReason.trim() || isRejecting) return;

    setIsRejecting(true);
    try {
      await loteService.rejectLote(rejectingLote.id, user, rejectReason.trim());
      alert("Solicitação de lote rejeitada com sucesso.");
      setRejectingLote(null);
      setRejectReason("");
      setShowRejectModal(false);
      loadLotes();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao rejeitar lote: " + e.message);
    } finally {
      setIsRejecting(false);
    }
  };

  // Enviar Solicitação da Oficina
  const handleSubmitRequest = async () => {
    if (!requestSku.trim() || requestQty <= 0 || !requestMotivo.trim() || isSubmittingRequest) return;

    setIsSubmittingRequest(true);
    try {
      const operatorName = activeOperator?.nome || user?.name || "Oficina";
      await loteService.requestLoteFromOficina(
        [{ sku: requestSku.toUpperCase().trim(), qtd: Number(requestQty) }],
        operatorName,
        requestMotivo
      );
      alert("Solicitação de lote enviada para o buffer com sucesso!");
      setShowRequestModal(false);
      setRequestSku("");
      setRequestQty(1);
      setRequestMotivo("");
      loadLotes();
    } catch (e) {
      alert("Erro ao enviar solicitação: " + e.message);
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 overflow-hidden">
      {/* Navbar Interna de Fábrica */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap justify-between items-center gap-3 shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          {selectedLoteId ? (
            <button 
              onClick={() => setSelectedLoteId(null)}
              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-800 transition-all flex items-center gap-1 text-xs font-bold"
            >
              <ArrowLeft size={16} /> Voltar para a lista de Lotes
            </button>
          ) : (
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button
                onClick={() => setViewMode("lotes")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  viewMode === "lotes" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Boxes size={14} /> Visão por Lotes
              </button>
              <button
                onClick={() => setViewMode("transversal")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  viewMode === "transversal" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Layers size={14} /> Visão Clássica por Status
              </button>
            </div>
          )}
        </div>

        {/* Botões de Ação na Barra Superior */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateLoteModal(true)}
            className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <Plus size={16} /> Criar Lote de Produção
          </button>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 overflow-auto p-4 md:p-6 custom-scrollbar">
        {selectedLote ? (
          /* WORKSPACE DO LOTE SELECIONADO */
          <div className="max-w-7xl mx-auto space-y-5 animate-fade-in">
            {/* Header do Lote */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-black text-slate-800">{selectedLote.codigo}</h2>
                  <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full ${
                    selectedLote.status === "em_producao" ? "bg-blue-100 text-blue-800" :
                    selectedLote.status === "suspenso" ? "bg-red-100 text-red-800 border border-red-200" :
                    "bg-emerald-100 text-emerald-800"
                  }`}>
                    {selectedLote.status}
                  </span>
                  <span className="text-xs text-slate-400 font-bold uppercase">Origem: {selectedLote.origem}</span>
                </div>
                {selectedLote.observacoes && <p className="text-xs text-slate-500 mt-1 italic">Obs: {selectedLote.observacoes}</p>}
              </div>

              {/* Ações Administrativas do Lote */}
              {selectedLote.id !== "AVULSO" && (
                <div className="flex items-center gap-2">
                  {selectedLote.status === "em_producao" && (
                    <button
                      onClick={() => {
                        setSelectedLoteMotivoId("");
                        setSuspendLoteObs("");
                        setShowSuspendLoteModal(true);
                      }}
                      className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs font-bold hover:bg-red-100 transition-all flex items-center gap-1 shadow-2xs"
                      title="Suspender produção de todas as peças deste lote"
                    >
                      <X size={14} /> Suspender Lote
                    </button>
                  )}
                  {selectedLote.status === "suspenso" && (
                    <button
                      onClick={handleReactivateLote}
                      className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold hover:bg-emerald-100 transition-all flex items-center gap-1 shadow-2xs"
                      title="Retornar lote e todas as suas peças suspensas para produção"
                    >
                      <RotateCcw size={14} /> Reativar Lote
                    </button>
                  )}
                  {inventory.filter(i => i.lote === selectedLote.id).length === 0 && (
                    <button
                      onClick={handleDeleteLote}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1 shadow-2xs cursor-pointer"
                      title="Excluir este lote do banco de dados"
                    >
                      <Trash2 size={14} /> Excluir Lote
                    </button>
                  )}
                </div>
              )}

              {/* Ações em Massa no Lote */}
              {selectedItemIds.size > 0 && (
                <div className="flex items-center gap-2 bg-purple-50 p-2 rounded-xl border border-purple-200">
                  <span className="text-xs font-bold text-purple-700 px-2">{selectedItemIds.size} selecionadas</span>
                  <button
                    onClick={() => handleBulkAdvanceStatus("pe_imprimindo")}
                    className="px-2.5 py-1 bg-blue-600 text-white rounded text-[11px] font-bold hover:bg-blue-700"
                  >
                    Mover Impressão
                  </button>
                  <button
                    onClick={() => handleBulkAdvanceStatus("pe_fundicao")}
                    className="px-2.5 py-1 bg-orange-600 text-white rounded text-[11px] font-bold hover:bg-orange-700"
                  >
                    Mover Fundição
                  </button>
                  {!isOficina && (
                    <button
                      onClick={() => handleBulkAdvanceStatus("pe_conferencia")}
                      className="px-2.5 py-1 bg-purple-600 text-white rounded text-[11px] font-bold hover:bg-purple-700"
                    >
                      Mover Conferência
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Resumo visual do Lote (sem duplicar as observações) */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <LoteSummary 
                progress={selectedLoteStats.progress} 
                costs={selectedLoteStats.costs} 
                regressions={selectedLoteStats.regressions} 
                observacoes={null} 
              />
            </div>

            {/* Grade de Peças do Lote */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Peças em Fabricação neste Lote</h3>
              {selectedLoteItems.length === 0 ? (
                <div className="bg-white p-8 rounded-xl border border-slate-200 text-center text-slate-400 italic">
                  Nenhuma peça física ativa encontrada para este lote.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {selectedLoteItems.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      catalogItem={findCatalogItem(item.sku)}
                      isSelected={selectedItemIds.has(item.id)}
                      currentConfig={PE_STATUS_CONFIG.find(c => c.id === item.status) || PE_STATUS_CONFIG[0]}
                      onToggleSelect={handleToggleSelect}
                      onDelete={handleDeleteItem}
                      onSuspend={handleOpenSuspend}
                      onReactivate={handleReactivateItem}
                      isLoteCancelado={item.lote && canceledLoteIds.has(item.lote)}
                      onConvertLoteToAvulso={handleConvertLoteToAvulso}
                      bomSkuSet={bomSkuSet}
                      user={user}
                      onAdvanceStatus={handleAdvanceStatus}
                      isOficina={isOficina}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : viewMode === "lotes" ? (
          /* VISÃO POR LOTES (PADRÃO) */
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Painel de Solicitações Pendentes (Sanfona/Dropdown) */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => setShowPendingPanel(!showPendingPanel)}
                className="w-full px-5 py-4 flex items-center justify-between font-black text-slate-800 hover:bg-slate-50/50 transition-all text-sm cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <ClipboardList className="text-purple-600" size={18} />
                  <span>Solicitações Pendentes de Lotes</span>
                  {pendingLotes.length > 0 && (
                    <span className="bg-amber-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full animate-bounce">
                      {pendingLotes.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 font-bold">
                    {showPendingPanel ? "Recolher" : "Visualizar"}
                  </span>
                  <ChevronRight size={16} className={`transform transition-transform ${showPendingPanel ? "rotate-90" : ""}`} />
                </div>
              </button>

              {showPendingPanel && (
                <div className="border-t border-slate-150 p-5 bg-slate-50/30 space-y-4">
                  {pendingLotes.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 italic text-xs">
                      Nenhuma solicitação pendente no momento. Todas foram processadas!
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingLotes.map(lote => {
                        const isApproving = approvingLoteId === lote.id;
                        
                        // Resolve itens declarados no lote
                        let rawItems = lote.items || lote.itens_planejados || lote.items_json || [];
                        if (typeof rawItems === "string") {
                          try { rawItems = JSON.parse(rawItems); } catch(e) {}
                        }
                        const displayItems = Array.isArray(rawItems) ? rawItems : [];
                        const totalPcs = displayItems.reduce((acc, curr) => acc + (Number(curr.qtd) || 1), 0);

                        return (
                          <div 
                            key={lote.id} 
                            className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
                          >
                            <div className="space-y-3 flex-1 min-w-0 w-full">
                              <div className="flex flex-wrap items-center gap-3">
                                <span className="font-black text-sm text-slate-800">{lote.codigo}</span>
                                <span className="text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">
                                  {lote.status}
                                </span>
                                <span className="text-[10px] font-bold text-slate-500 uppercase bg-slate-100 px-2 py-0.5 rounded">
                                  Origem: {lote.origem || "Manual"}
                                </span>
                                <span className="text-[10px] font-bold text-slate-500 font-medium">
                                  Solicitado em: {new Date(lote.created).toLocaleDateString("pt-BR")}
                                </span>
                              </div>

                              {lote.observacoes && (
                                <p className="text-xs text-slate-600 italic bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                                  Obs: {lote.observacoes}
                                </p>
                              )}

                              {displayItems.length === 0 ? (
                                <p className="text-xs text-slate-400 italic font-medium">Nenhum item discriminado no lote.</p>
                              ) : (
                                <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200 space-y-2">
                                  <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                                    <Package size={14} className="text-purple-600" />
                                    Itens Solicitados ({totalPcs} un):
                                  </span>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {displayItems.map((item, idx) => {
                                      const cat = resolveCatalogSku(item.sku, catalogMap) || (findCatalogItem ? findCatalogItem(item.sku) : null);
                                      return (
                                        <div key={idx} className="bg-white p-2 rounded-lg border border-slate-200 flex items-center justify-between gap-2 shadow-2xs">
                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className="font-extrabold text-[11px] text-purple-700">{item.sku}</span>
                                              <span className="bg-purple-100 text-purple-850 text-[9px] font-black px-1.5 py-0.2 rounded">
                                                x{item.qtd || 1}
                                              </span>
                                              {cat?.isInactive && (
                                                <span className="bg-slate-100 text-slate-500 text-[8px] font-bold px-1.5 py-0.2 rounded border border-slate-200">
                                                  Inativo na loja
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[10px] text-slate-500 truncate font-semibold mt-0.5">
                                              {item.name || cat?.name || "Produto sem cadastro"}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Ações de Aprovação */}
                            <div className="flex sm:flex-row md:flex-col items-stretch md:items-end gap-2 shrink-0 w-full md:w-auto">
                              <button
                                onClick={() => handleApproveLote(lote.id)}
                                disabled={isApproving}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl text-xs font-black shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer flex-1 md:flex-none"
                              >
                                <Check size={14} /> {isApproving ? "Aprovando..." : "Aprovar Lote"}
                              </button>
                              <button
                                onClick={() => {
                                  setRejectingLote(lote);
                                  setRejectReason("");
                                  setShowRejectModal(true);
                                }}
                                disabled={isApproving}
                                className="px-4 py-2 bg-red-50 hover:bg-red-100 disabled:bg-slate-100 text-red-650 border border-red-200 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 cursor-pointer flex-1 md:flex-none"
                              >
                                <X size={14} /> Rejeitar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Gerenciamento de Lotes (Grid / Lista) */}
            <div className="space-y-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Boxes size={16} className="text-purple-600" />
                Lotes em Fabricação / Gerenciamento
              </h3>

              {/* Barra de Filtros */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                  <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar lote por código ou SKU..."
                      className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:border-purple-500 text-slate-800"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select
                    className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 outline-none"
                    value={filterOrigem}
                    onChange={(e) => setFilterOrigem(e.target.value)}
                  >
                    <option value="TODOS">Todas as Origens</option>
                    <option value="planejamento">Planejamento</option>
                    <option value="escritorio">Escritório</option>
                    <option value="solicitacao_oficina">Oficina</option>
                  </select>
                </div>
              </div>

              {/* Lista Gerencial em Formato de Tabela */}
              {loadingLotes ? (
                <div className="text-center py-12 text-slate-400 text-xs italic">Carregando lotes da fábrica...</div>
              ) : activeLotesFiltered.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center text-slate-400 italic">
                  Nenhum lote ativo encontrado com os filtros selecionados.
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-wider">
                          <th className="px-6 py-4">Lote</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Origem</th>
                          <th className="px-6 py-4">Qtd Peças</th>
                          <th className="px-6 py-4">Data Solicitação</th>
                          <th className="px-6 py-4 text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs font-bold text-slate-700">
                        {activeLotesFiltered.map(lote => {
                          const qty = getLoteItemCount(lote.id);
                          const isSuspenso = lote.status === "suspenso";
                          
                          return (
                            <tr 
                              key={lote.id} 
                              onClick={() => setSelectedLoteId(lote.id)}
                              className="hover:bg-slate-50/70 transition-all cursor-pointer group"
                            >
                              <td className="px-6 py-4">
                                <span className="font-extrabold text-slate-900 group-hover:text-purple-600 transition-colors">
                                  {lote.codigo}
                                </span>
                                {lote.observacoes && (
                                  <p className="text-[10px] text-slate-400 font-medium italic mt-0.5 max-w-md truncate">
                                    {lote.observacoes}
                                  </p>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`text-[9px] uppercase font-black px-2 py-0.5 rounded-full border ${
                                  isSuspenso 
                                    ? "bg-red-50 text-red-700 border-red-200" 
                                    : lote.id === "AVULSO"
                                    ? "bg-slate-100 text-slate-600 border-slate-200"
                                    : "bg-blue-50 text-blue-700 border-blue-200"
                                }`}>
                                  {isSuspenso ? "Suspenso" : lote.id === "AVULSO" ? "Avulso" : "Em Produção"}
                                </span>
                              </td>
                              <td className="px-6 py-4 uppercase text-[10px] text-slate-500">
                                {lote.origem}
                              </td>
                              <td className="px-6 py-4 text-slate-900">
                                {qty} {qty === 1 ? "peça" : "peças"}
                              </td>
                              <td className="px-6 py-4 text-slate-500 font-medium">
                                {getLoteDate(lote)}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedLoteId(lote.id);
                                  }}
                                  className="px-3 py-1.5 bg-slate-100 hover:bg-purple-600 hover:text-white rounded-lg text-[10px] font-black text-slate-600 transition-all shadow-3xs"
                                >
                                  Gerenciar
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* VISÃO CLÁSSICA COMPLETA POR STATUS (EXATAMENTE COMO ERA) */
          <StockProductionTab
            user={user}
            findCatalogItem={findCatalogItem}
            inventory={inventory}
            canceledLoteIds={canceledLoteIds}
            onRefresh={onRefresh}
          />
        )}
      </div>

      {/* MODAL DE SUSPENSÃO DE PEÇA */}
      {suspendModalItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-800">Suspender Produção de Peça</h3>
              <button onClick={() => setSuspendModalItem(null)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-slate-500">
              Selecione o motivo da suspensão para a peça <span className="font-bold text-blue-600">{suspendModalItem.sku}</span>. 
              Insumos BOM debitados serão restaurados ao estoque.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Motivo da Suspensão</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none"
                  value={selectedMotivoId}
                  onChange={(e) => setSelectedMotivoId(e.target.value)}
                >
                  <option value="">Selecione um motivo...</option>
                  {motivosSuspensao.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.nome} {m.obs_obrigatoria ? "(Obs Obrigatória)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Observações Justificativas</label>
                <textarea
                  placeholder="Descreva o motivo detalhado..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs outline-none focus:border-purple-500 text-slate-800 h-20"
                  value={suspendObs}
                  onChange={(e) => setSuspendObs(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() => setSuspendModalItem(null)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSuspend}
                disabled={!selectedMotivoId || isSuspending}
                className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm disabled:bg-red-300"
              >
                Confirmar Suspensão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE SOLICITAÇÃO DA OFICINA */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-800">Solicitar Produção de Lote (Oficina)</h3>
              <button onClick={() => setShowRequestModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">SKU da Peça</label>
                <input
                  type="text"
                  placeholder="Ex: ANL-01-PR"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-purple-500 text-slate-800 uppercase"
                  value={requestSku}
                  onChange={(e) => setRequestSku(e.target.value)}
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Quantidade</label>
                <input
                  type="number"
                  min="1"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-purple-500 text-slate-800"
                  value={requestQty}
                  onChange={(e) => setRequestQty(e.target.value)}
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Justificativa / Motivo da Solicitação</label>
                <textarea
                  placeholder="Por que esta produção está sendo solicitada..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs outline-none focus:border-purple-500 text-slate-800 h-20"
                  value={requestMotivo}
                  onChange={(e) => setRequestMotivo(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowRequestModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmitRequest}
                disabled={!requestSku.trim() || !requestMotivo.trim() || isSubmittingRequest}
                className="px-4 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-sm disabled:bg-purple-300"
              >
                Enviar para o Buffer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CRIAÇÃO MANUAL DE LOTE ("MEUS LOTES") */}
      {showCreateLoteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                <Boxes className="text-purple-600" size={18} /> Criar Lote de Produção Manual
              </h3>
              <button onClick={() => setShowCreateLoteModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar pr-1">
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Nome/Identificador do Lote (Opcional)</label>
                <input
                  type="text"
                  placeholder="Ex: LOTE-REPOSICAO-URGENTE"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-purple-500 text-slate-800"
                  value={newLoteCodigo}
                  onChange={(e) => setNewLoteCodigo(e.target.value)}
                />
              </div>

              {/* Lista de SKUs e Quantidades */}
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-slate-500 uppercase block">Itens do Lote (SKUs + Quantidade)</label>
                {newLoteItems.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="SKU da peça (ex: RIN-LIA-Z-15)"
                      className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono font-bold uppercase outline-none focus:border-purple-500 text-slate-800"
                      value={row.sku}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewLoteItems(prev => prev.map((item, i) => i === idx ? { ...item, sku: val } : item));
                      }}
                    />
                    <input
                      type="number"
                      min="1"
                      placeholder="Qtd"
                      className="w-16 bg-slate-50 border border-slate-200 rounded-xl px-2 py-2 text-xs font-bold text-center outline-none focus:border-purple-500 text-slate-800"
                      value={row.qtd}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewLoteItems(prev => prev.map((item, i) => i === idx ? { ...item, qtd: val } : item));
                      }}
                    />

                    {/* Botão de Análise Pontual LAZY do SKU */}
                    {row.sku.trim() !== "" && (
                      <button
                        type="button"
                        onClick={() => setAnalyzingSku(row.sku.trim().toUpperCase())}
                        className="p-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-xl transition-all"
                        title="Analisar situação deste SKU no estoque/demandas"
                      >
                        <Eye size={16} />
                      </button>
                    )}

                    {newLoteItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setNewLoteItems(prev => prev.filter((_, i) => i !== idx))}
                        className="p-2 text-slate-400 hover:text-red-600 rounded-xl"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => setNewLoteItems(prev => [...prev, { sku: "", qtd: 1 }])}
                  className="text-xs font-bold text-purple-600 hover:text-purple-800 flex items-center gap-1 pt-1"
                >
                  <Plus size={14} /> Adicionar outro SKU
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 shrink-0">
              <button
                type="button"
                onClick={() => setShowCreateLoteModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  const validItems = newLoteItems.filter(i => i.sku.trim() !== "" && Number(i.qtd) > 0);
                  if (validItems.length === 0) {
                    alert("Insira ao menos um SKU com quantidade válida.");
                    return;
                  }
                  try {
                    const author = activeOperator?.nome || user?.name || "Gestor";
                    await loteService.requestLoteFromOficina(
                      validItems.map(i => ({ sku: i.sku.trim().toUpperCase(), qtd: Number(i.qtd) })),
                      author,
                      newLoteCodigo ? `Lote Manual: ${newLoteCodigo}` : "Lote Manual de Produção"
                    );
                    alert("Lote enviado para o buffer de aprovações com sucesso!");
                    setShowCreateLoteModal(false);
                    setNewLoteItems([{ sku: "", qtd: 1 }]);
                    setNewLoteCodigo("");
                    if (onRefresh) onRefresh();
                  } catch (e) {
                    alert("Erro ao criar lote: " + e.message);
                  }
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-sm"
              >
                Criar e Enviar para Aprovação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE ANÁLISE PONTUAL DE SKU (LAZY) */}
      {analyzingSku && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[60] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-600" /> Análise Pontual do SKU
                </h3>
                <span className="text-xs font-extrabold text-purple-700 font-mono">{analyzingSku}</span>
              </div>
              <button onClick={() => setAnalyzingSku(null)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            {/* Diagnóstico Resolvido pelo `resolveCatalogSku` */}
            {(() => {
              const cat = findCatalogItem ? findCatalogItem(analyzingSku) : null;
              const readyStock = Array.isArray(inventory) ? inventory.filter(i => i.sku === analyzingSku && i.status === "in_stock" && !i.isPE).length : 0;
              const inProduction = Array.isArray(inventory) ? inventory.filter(i => i.sku === analyzingSku && i.isPE && !["in_stock", "sold", "adjusted_out"].includes(i.status)).length : 0;
              const minStock = cat?.min_stock || cat?.estoque_minimo_definido || 0;

              return (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-slate-700">
                    Produto: <span className="text-slate-500 font-normal">{cat?.name || "Produto sem cadastro no catálogo"}</span>
                  </p>

                  <div className="grid grid-cols-3 gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block uppercase">Pronto</span>
                      <span className="text-sm font-black text-emerald-600">{readyStock} pçs</span>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block uppercase">Em Produção</span>
                      <span className="text-sm font-black text-blue-600">{inProduction} pçs</span>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block uppercase">Estoque Mínimo</span>
                      <span className="text-sm font-black text-purple-600">{minStock} pçs</span>
                    </div>
                  </div>

                  <div className="bg-purple-50/70 p-3 rounded-xl border border-purple-200 text-xs text-purple-900 font-bold space-y-1">
                    <p>💡 Recomendação do Sistema:</p>
                    <p className="font-normal text-[11px]">
                      {readyStock + inProduction < minStock 
                        ? `Atenção: A disponibilidade atual (${readyStock + inProduction}) está abaixo do mínimo (${minStock}). Recomendado produzir ao menos ${minStock - (readyStock + inProduction)} peça(s).`
                        : `Estoque saudável. A cobertura atual de ${readyStock + inProduction} peça(s) atende o estoque mínimo definido.`}
                    </p>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setAnalyzingSku(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold"
              >
                Fechar Análise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE SUSPENSÃO DE LOTE INTEIRO */}
      {showSuspendLoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-800">Suspender Lote Completo</h3>
              <button onClick={() => setShowSuspendLoteModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-slate-500">
              Selecione o motivo da suspensão para o lote <span className="font-bold text-blue-600">{selectedLote?.codigo}</span>. 
              Isso irá suspender <strong>todas as peças ativas</strong> deste lote e restaurar os respectivos insumos BOM ao estoque.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Motivo da Suspensão</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none"
                  value={selectedLoteMotivoId}
                  onChange={(e) => setSelectedLoteMotivoId(e.target.value)}
                >
                  <option value="">Selecione um motivo...</option>
                  {motivosSuspensao.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.nome} {m.obs_obrigatoria ? "(Obs Obrigatória)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">Observações Justificativas</label>
                <textarea
                  placeholder="Descreva o motivo detalhado..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs outline-none focus:border-purple-500 text-slate-800 h-20"
                  value={suspendLoteObs}
                  onChange={(e) => setSuspendLoteObs(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowSuspendLoteModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSuspendLote}
                disabled={!selectedLoteMotivoId || isSuspendingLote}
                className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 rounded-xl shadow-sm transition-all"
              >
                {isSuspendingLote ? "Suspendendo..." : "Confirmar Suspensão"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE REJEIÇÃO DE SOLICITAÇÃO */}
      {showRejectModal && rejectingLote && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-scale-in">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-800">Rejeitar Solicitação de Lote</h3>
              <button 
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingLote(null);
                }} 
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>
            
            <p className="text-xs text-slate-500">
              Descreva o motivo da rejeição do lote <strong>{rejectingLote.codigo}</strong>. Essa informação será salva no histórico.
            </p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex: SKU incorreto, insumo indisponível na oficina..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold text-slate-800 outline-none focus:border-purple-500 h-24 resize-none"
            />

            <div className="flex gap-2 justify-end pt-3 border-t border-slate-100">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingLote(null);
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmRejectLote}
                disabled={!rejectReason.trim() || isRejecting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-350 text-white rounded-xl text-xs font-black shadow-md transition-all"
              >
                {isRejecting ? "Rejeitando..." : "Rejeitar Solicitação"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
